<template>
  <div
    class="crm-sidebar"
    :class="{
      'crm-sidebar--collapsed': isCollapsed && !isHovered,
      'crm-sidebar--hovered': isCollapsed && isHovered,
    }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="crm-sidebar__glow" />

    <!-- Brand -->
    <div class="crm-sidebar__brand">
      <div class="crm-sidebar__brand-icon">
        <feather-icon
          icon="ZapIcon"
          size="22"
        />
      </div>
      <div class="crm-sidebar__brand-text">
        <span class="crm-sidebar__brand-name">Gulftic</span>
        <span class="crm-sidebar__brand-tag">CRM System</span>
      </div>
      <button
        class="crm-sidebar__toggle-btn"
        :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="toggleCollapsed"
      >
        <feather-icon
          :icon="isCollapsed ? 'ChevronsRightIcon' : 'ChevronsLeftIcon'"
          size="16"
        />
      </button>
    </div>

    <!--
      MID-CRM X6 / BRD US 6.2 — one search across companies, contacts, opportunities
      and leads (CR number, name, phone). Hidden when the sidebar is collapsed, where
      there is no room for a result list.
    -->
    <div
      v-if="!isCollapsed || isHovered"
      class="crm-sidebar__search"
    >
      <global-search />
    </div>

    <!-- User card -->
    <div class="crm-sidebar__user-card">
      <div class="crm-sidebar__user-avatar">
        <feather-icon
          icon="UserIcon"
          size="20"
        />
      </div>
      <div class="crm-sidebar__user-info">
        <span class="crm-sidebar__user-name">{{ userDisplayName }}</span>
        <span class="crm-sidebar__user-role">{{ userRole }}</span>
      </div>
    </div>

    <!-- Navigation -->
    <nav class="crm-sidebar__nav">
      <div
        v-for="section in filteredNavSections"
        :key="section.label"
        class="crm-sidebar__section"
      >
        <span class="crm-sidebar__section-label">{{ section.label }}</span>
        <ul class="crm-sidebar__list">
          <li
            v-for="item in section.items"
            :key="`${section.label}-${item.label}`"
            class="crm-sidebar__item"
          >
            <router-link
              :to="{ name: item.route }"
              class="crm-sidebar__link"
              active-class="crm-sidebar__link--active"
            >
              <span
                class="crm-sidebar__link-icon"
                :class="`crm-sidebar__link-icon--${item.color}`"
              >
                <feather-icon
                  :icon="item.icon"
                  size="17"
                />
              </span>
              <span class="crm-sidebar__link-label">{{ item.label }}</span>
              <span
                v-if="item.badge"
                class="crm-sidebar__link-badge"
                :class="`crm-sidebar__link-badge--${item.badgeVariant || 'primary'}`"
              >
                {{ item.badge }}
              </span>
            </router-link>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Footer -->
    <div class="crm-sidebar__footer">
      <button
        class="crm-sidebar__footer-btn crm-sidebar__footer-btn--profile"
        title="Go back to main dashboard"
        @click="$router.push({ name: 'dashboard' })"
      >
        <feather-icon
          icon="HomeIcon"
          size="16"
        />
        <span>Main App</span>
      </button>
      <button
        class="crm-sidebar__footer-btn crm-sidebar__footer-btn--logout"
        @click="logout"
      >
        <feather-icon
          icon="LogOutIcon"
          size="16"
        />
        <span>Logout</span>
      </button>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import navMenuItems from '@/navigation/crm-vertical'
import GlobalSearch from '@/views/crm/components/GlobalSearch.vue'

export default {
  name: 'VerticalNavMenuCRM',
  components: { GlobalSearch },
  data() {
    return {
      userRole: '',
      permissions: [],
      userDisplayName: 'User',
      isHovered: false,
      hoverTimeout: null,
      navSections: navMenuItems,
    }
  },
  computed: {
    isCollapsed() {
      return store.state.verticalMenu.isVerticalMenuCollapsed
    },

    /**
     * The user's role plus any granted permissions — exactly how the main admin
     * panel builds it in VerticalNavMenu.vue. Reusing that rule means the CRM
     * behaves like the rest of the system instead of inventing its own.
     */
    allowedRoles() {
      return new Set([this.userRole, ...this.permissions].filter(Boolean))
    },

    /**
     * Show a salesperson only what belongs to him. An empty section is dropped
     * entirely rather than left as an empty heading.
     *
     * Presentation only — the data itself is scoped server-side by CrmAccess.
     */
    filteredNavSections() {
      const allowed = this.allowedRoles

      const sections = this.navSections
        .map(section => {
          const items = (section.items || []).filter(
            item => Array.isArray(item.role) && item.role.some(r => allowed.has(r)),
          )
          return items.length ? { ...section, items } : null
        })
        .filter(Boolean)

      // SAFETY: an unrecognised role (a new role, a missing localStorage value)
      // would otherwise leave the user staring at an EMPTY sidebar — a worse
      // failure than showing too much. Fall back to the salesperson's own set;
      // their data is still scoped server-side by CrmAccess, and the
      // manager-only endpoints answer 403 regardless of what is on screen.
      if (!sections.length) {
        return this.navSections
          .map(section => {
            const items = (section.items || []).filter(
              item => Array.isArray(item.role) && item.role.includes('markter'),
            )
            return items.length ? { ...section, items } : null
          })
          .filter(Boolean)
      }

      return sections
    },
  },
  mounted() {
    this.userRole = localStorage.getItem('accessRole') || 'Member'

    // Same source the admin panel reads (VerticalNavMenu.vue): a user's extra
    // permissions count alongside their role. Stored as JSON or a comma list
    // depending on the login payload, so accept both and never throw here — a
    // parse error must not blank the whole CRM menu.
    const raw = localStorage.getItem('permissions')
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        this.permissions = Array.isArray(parsed) ? parsed : String(raw).split(',')
      } catch (e) {
        this.permissions = String(raw).split(',')
      }
      this.permissions = this.permissions.map(p => String(p).trim()).filter(Boolean)
    }

    const rawName = localStorage.getItem('userName') || localStorage.getItem('name')
    if (rawName) this.userDisplayName = rawName
  },
  methods: {
    toggleCollapsed() {
      store.commit('verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED', !this.isCollapsed)
    },
    onMouseEnter() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
        this.hoverTimeout = null
      }
      this.isHovered = true
    },
    onMouseLeave() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
      }
      this.hoverTimeout = setTimeout(() => {
        this.isHovered = false
        this.hoverTimeout = null
      }, 300)
    },
    logout() {
      localStorage.removeItem('access_token')
      localStorage.removeItem('accessToken')
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        const authChannel = new BroadcastChannel('auth-sync')
        authChannel.postMessage({ type: 'logout' })
        authChannel.close()
      }
      this.$router.push({ name: 'login' })
    },
  },
}
</script>

<style lang="scss">
// ── Palette variables ──────────────────────────────────────
// bg: clean white sidebar
// text-primary: dark slate for main text
// text-muted: medium grey for hints
// accent: vivid purple

.crm-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 260px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  border-right: 1px solid #e8eaf0;
  box-shadow: 4px 0 24px rgba(115, 103, 240, 0.08);
  transition: width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.crm-sidebar__glow {
  position: absolute;
  top: -60px;
  left: -40px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(115, 103, 240, 0.1), transparent 65%);
  pointer-events: none;
  z-index: 0;
}

/* ── Brand ─────────────────────────────────────────────── */
.crm-sidebar__brand {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 1.25rem 1rem 1rem;
  border-bottom: 1px solid #f0f1f6;
}

.crm-sidebar__brand-icon {
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: linear-gradient(135deg, #7367f0, #00cfe8);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 18px rgba(115, 103, 240, 0.3);
}

.crm-sidebar__brand-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.crm-sidebar__brand-name {
  font-size: 1rem;
  font-weight: 800;
  color: #2d2d3f;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.crm-sidebar__brand-tag {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #7367f0;
}

.crm-sidebar__toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  background: #f3f2ff;
  color: #7367f0;
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0;
  pointer-events: none;
  transition: background 0.15s ease, color 0.15s ease, opacity 0.2s ease;

  &:hover {
    background: #7367f0;
    color: #fff;
  }
}

.crm-sidebar:hover .crm-sidebar__toggle-btn {
  opacity: 1;
  pointer-events: auto;
}

/* ── Global search (MID-CRM X6) ─────────────────────────── */
.crm-sidebar__search {
  padding: 0 0.85rem 0.6rem;
  position: relative;
  z-index: 20; /* the result list must float above the nav below it */
}

/* ── User card ──────────────────────────────────────────── */
.crm-sidebar__user-card {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin: 0.75rem 0.85rem;
  padding: 0.7rem 0.85rem;
  border-radius: 14px;
  background: #f5f4ff;
  border: 1px solid #e4e1fd;
}

.crm-sidebar__user-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7367f0, #00cfe8);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.crm-sidebar__user-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.crm-sidebar__user-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #2d2d3f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.crm-sidebar__user-role {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  color: #9e9bb5;
}

/* ── Nav ────────────────────────────────────────────────── */
.crm-sidebar__nav {
  position: relative;
  z-index: 1;
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 0 0.5rem;

  scrollbar-width: thin;
  scrollbar-color: #e0dff5 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #e0dff5;
    border-radius: 4px;
  }
}

.crm-sidebar__section {
  margin-bottom: 0.25rem;
}

.crm-sidebar__section-label {
  display: block;
  padding: 0.6rem 1.1rem 0.2rem;
  font-size: 0.62rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #b0adc8;
}

.crm-sidebar__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.crm-sidebar__item {
  padding: 0.15rem 0.65rem;
}

.crm-sidebar__link {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 0.75rem;
  border-radius: 12px;
  text-decoration: none;
  color: #5e5c78;
  transition: background 0.18s ease, color 0.18s ease;

  &:hover {
    background: #f5f4ff;
    color: #7367f0;
    text-decoration: none;
  }

  &--active {
    background: #f0eeff;
    color: #7367f0;

    .crm-sidebar__link-label {
      font-weight: 700;
    }

    .crm-sidebar__link-icon {
      box-shadow: 0 4px 14px rgba(115, 103, 240, 0.3);
    }
  }
}

.crm-sidebar__link-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: box-shadow 0.18s ease;

  &--primary {
    background: #ede9fd;
    color: #7367f0;
  }

  &--info {
    background: #e0f7fb;
    color: #00b8d4;
  }

  &--success {
    background: #e2f9ee;
    color: #28a745;
  }

  &--warning {
    background: #fff4e5;
    color: #e08b00;
  }

  &--danger {
    background: #fdecea;
    color: #d93025;
  }
}

.crm-sidebar__link-label {
  flex: 1;
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1;
}

.crm-sidebar__link-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 0.4rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 800;

  &--primary {
    background: #ede9fd;
    color: #7367f0;
  }

  &--warning {
    background: #fff4e5;
    color: #e08b00;
  }

  &--danger {
    background: #fdecea;
    color: #d93025;
  }
}

/* ── Hovered peek state (collapsed but mouse is over it) ── */
.crm-sidebar--hovered {
  width: 260px;
  box-shadow: 6px 0 32px rgba(115, 103, 240, 0.15);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, #7367f0, #00cfe8);
    opacity: 0.7;
    pointer-events: none;
  }
}

/* ── Collapsed state ────────────────────────────────────── */
.crm-sidebar--collapsed {
  width: 80px;

  .crm-sidebar__brand-text,
  .crm-sidebar__user-info,
  .crm-sidebar__section-label,
  .crm-sidebar__link-label,
  .crm-sidebar__link-badge,
  .crm-sidebar__footer-btn span {
    display: none;
  }

  .crm-sidebar__brand {
    justify-content: center;
    padding: 1.25rem 0 1rem;
    gap: 0;
  }

  .crm-sidebar__brand-icon {
    width: 44px;
    height: 44px;
    margin: 0 auto;
  }

  .crm-sidebar__toggle-btn {
    position: absolute;
    right: 8px;
    top: 18px;
    opacity: 0;
    pointer-events: none;
  }

  &:hover .crm-sidebar__toggle-btn {
    opacity: 1;
    pointer-events: auto;
  }

  .crm-sidebar__user-card {
    justify-content: center;
    padding: 0.7rem 0;
    margin: 0.75rem 0.6rem;
  }

  .crm-sidebar__item {
    padding: 0 0.5rem;
  }

  .crm-sidebar__link {
    justify-content: center;
    padding: 0.6rem 0;
    gap: 0;
  }

  .crm-sidebar__link-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }

  .crm-sidebar__footer {
    flex-direction: column;
    gap: 0.4rem;
    padding: 0.75rem 0.5rem;
  }

  .crm-sidebar__footer-btn {
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 12px;
    flex: unset;
  }
}

/* ── Footer ─────────────────────────────────────────────── */
.crm-sidebar__footer {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem;
  border-top: 1px solid #f0f1f6;
}

.crm-sidebar__footer-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem 0.65rem;
  border-radius: 10px;
  border: none;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &--profile {
    background: #f3f2ff;
    color: #7367f0;

    &:hover {
      background: #7367f0;
      color: #fff;
    }
  }

  &--logout {
    background: #fdecea;
    color: #d93025;

    &:hover {
      background: #d93025;
      color: #fff;
    }
  }
}
</style>
